import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Image, Upload } from "lucide-react";

interface LogoUploadProps {
  onLogoUpload: (url: string) => void;
  existingLogoUrl?: string;
}

const LogoUpload = ({ onLogoUpload, existingLogoUrl }: LogoUploadProps) => {
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(existingLogoUrl || null);

  // Update previewUrl when existingLogoUrl changes
  useEffect(() => {
    if (existingLogoUrl) {
      setPreviewUrl(existingLogoUrl);
    }
  }, [existingLogoUrl]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file type",
        description: "Please upload an image file (JPEG, PNG, etc.)",
        variant: "destructive",
      });
      return;
    }

    // Validate specific image types
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Unsupported image type",
        description: "Please upload a JPEG, PNG, GIF, or WebP image.",
        variant: "destructive",
      });
      return;
    }

    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please upload an image smaller than 2MB",
        variant: "destructive",
      });
      return;
    }

    try {
      // Create a local preview URL immediately
      const localPreviewUrl = URL.createObjectURL(file);
      setPreviewUrl(localPreviewUrl);
      
      setIsUploading(true);

      // Check auth status before proceeding
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.error("User not authenticated for upload");
        toast({
          title: "Authentication required",
          description: "Please sign in to upload a logo.",
          variant: "destructive",
        });
        setIsUploading(false);
        return;
      }

      // Generate a unique file name with correct extension based on mime type
      const mimeToExt = {
        'image/jpeg': 'jpg',
        'image/png': 'png',
        'image/gif': 'gif',
        'image/webp': 'webp'
      };
      const fileExt = mimeToExt[file.type as keyof typeof mimeToExt] || 'png';
      const fileName = `${Math.random().toString(36).substring(2, 15)}_${Date.now()}.${fileExt}`;
      const filePath = fileName;

      console.log("Starting upload with file type:", file.type);
      console.log("File name:", fileName);
      
      // Read file as binary data
      const fileBuffer = await file.arrayBuffer();
      const fileData = new Uint8Array(fileBuffer);
      
      // Upload with correct content type based on file type
      const { data, error } = await supabase.storage
        .from('app_logos')
        .upload(filePath, fileData, {
          contentType: file.type, // Use the actual file type
          upsert: true
        });

      if (error) {
        console.error("Upload error:", error);
        throw error;
      }

      console.log("Upload successful, getting public URL");

      // Get public URL
      const { data: publicUrlData } = supabase.storage
        .from('app_logos')
        .getPublicUrl(filePath);

      const logoUrl = publicUrlData.publicUrl;
      console.log("Public URL:", logoUrl);
      
      // Call the callback with the logo URL
      if (logoUrl) {
        onLogoUpload(logoUrl);
        
        toast({
          title: "Logo uploaded successfully",
          description: "Your app logo has been uploaded.",
        });
      } else {
        throw new Error("Failed to get public URL");
      }
    } catch (error: any) {
      console.error('Error uploading logo:', error);
      toast({
        title: "Error uploading logo",
        description: error?.message || "There was a problem uploading your logo. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-4">
        <div className="border rounded-lg overflow-hidden w-20 h-20 flex items-center justify-center bg-gray-50">
          {previewUrl ? (
            <img 
              src={previewUrl}
              alt="App logo preview" 
              className="w-full h-full object-contain"
              onError={() => {
                console.error("Error loading preview image");
                setPreviewUrl(null);
              }}
            />
          ) : (
            <Image className="w-8 h-8 text-gray-300" />
          )}
        </div>
        
        <div className="flex-1">
          <Button
            type="button"
            variant="outline"
            className="relative"
            disabled={isUploading}
          >
            <input
              type="file"
              className="absolute inset-0 opacity-0 cursor-pointer"
              onChange={handleFileChange}
              accept="image/*"
              disabled={isUploading}
            />
            <Upload className="mr-2 h-4 w-4" />
            {isUploading ? "Uploading..." : "Upload Logo"}
          </Button>
          <p className="text-sm text-gray-500 mt-2">
            Upload a square image, recommended size 100x100px. Max 2MB.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LogoUpload;
